// Ügyfél-lista (full-page) — a közös DataTable-re épül: szerver-rendezés (név), végtelen görgetés (100/lap),
// kereső (300 ms debounce) + státusz-szűrő. Jog-vezérelt akciók (clients.manage → új/szerkesztés,
// clients.delete → törlés). A régi ad-hoc táblát ez váltja ki (a többi oldal migrációja külön kör).
import { type ClientRowSchema } from '@atlas/contracts';
import { ArrowDownTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  BillingProfileCell,
  billingProfileExportText,
} from '@/components/clients/billing-profile-cell';
import { ClientStatusBadge } from '@/components/clients/client-status-badge';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListExportButton } from '@/components/shared/list-export-button';
import { ListNewButton } from '@/components/shared/list-new-button';
import {
  DeleteRowAction,
  EditRowAction,
  RowActions,
  ViewRowAction,
} from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { Text } from '@/components/ui/text';
import { clientKeys } from '@/features/clients/api';
import { useListExport } from '@/hooks/use-list-export';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// A SZŰRŐK search-paramok (a state-konvenció: a szűrt nézet legyen linkelhető és F5-biztos) — a route-nak
// ismernie kell a kulcsokat, különben a router kiszűri őket. A default-értékű kulcs nem kerül az URL-be.
const clientsSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  order: z.string().optional(),
});

export const Route = createFileRoute('/clients/')({
  validateSearch: (search) => clientsSearchSchema.parse(search),
  component: ClientsListPage,
});

type ClientRow = z.infer<typeof ClientRowSchema>;
type ListPage = Awaited<ReturnType<typeof api.clients.list.query>>;

const PAGE_SIZE = 100;
const ALL = '__all__';
const STATUS_OPTIONS = ['active', 'pending', 'inactive'];

export function ClientsListPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManage = can('clients.manage');
  const canDelete = can('clients.delete');

  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    status: ALL,
    order: 'asc',
  });
  const q = urlFilters.q;
  const status = urlFilters.status;
  const order = urlFilters.order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  // A rendezés-kulcs fixen `name` (szerver-oldal, magyar collation) — csak az irány váltható.
  const [deleteTarget, setDeleteTarget] = useState<ClientRow | null>(null);

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  const filters = { q, status, order };
  const listKey = clientKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam }) =>
      api.clients.list.query({
        query: {
          q: q || undefined,
          status: status === ALL ? undefined : status,
          order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.clients : [])),
    [pages],
  );
  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  // A query nem dob non-2xx-re, csak státuszt ad → a lapok státuszából olvassuk ki a hiba-állapotot.
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = infinite.isError || hasNoSuccess;
  // 401/403 → jog-hiba (általános „nincs jogod" üzenet); minden más hiba → általános betöltési hiba + retry.
  const isForbidden = pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters = q !== '' || status !== ALL;

  // Egyetlen rendezhető oszlop (név): a fejléc-kattintás CSAK az irányt váltja (a kulcs fix `name`).
  const toggleOrder = useCallback(
    () => setFilters({ order: order === 'asc' ? 'desc' : 'asc' }),
    [order, setFilters],
  );
  // A fetchNextPage a react-query-ből stabil referencia → a loadMore is stabil.
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  const removeMutation = useEntityMutation<{ id: string }, InfiniteData<ListPage>>({
    mutationFn: ({ id }) => api.clients.remove.mutation({ params: { id } }),
    invalidateKeys: [clientKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        if (!old) return old;
        const id = deleteTarget?.id;
        if (!id) return old;
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.status === 200
              ? {
                  ...p,
                  body: {
                    ...p.body,
                    clients: p.body.clients.filter((c) => c.id !== id),
                    total: Math.max(0, p.body.total - 1),
                  },
                }
              : p,
          ),
        };
      },
    },
    messages: {
      success: t('toast:clients.deleted'),
      conflict: t('toast:clients.conflict'),
      forbidden: t('toast:clients.forbidden'),
      validation: t('toast:clients.validation'),
      error: t('toast:clients.error'),
    },
  });

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate({ id: deleteTarget.id }, { onSettled: () => setDeleteTarget(null) });
  }

  // Halvány em-dash az üres tartalmi cellákhoz.
  const emDash = <span className="text-muted-foreground">—</span>;

  // EXPORT: a sorok a lista-végpontról, az AKTÍV szűrőkkel, 200-as lapokkal a végéig (nem a betöltött lap).
  const fetchExportPage = useCallback(
    async (offset: number | undefined) => {
      const r = await api.clients.list.query({
        query: {
          q: q || undefined,
          status: status === ALL ? undefined : status,
          order,
          limit: 200,
          offset: offset ?? 0,
        },
      });
      if (r.status !== 200) return null;
      return { rows: r.body.clients, next: r.body.nextOffset };
    },
    [q, status, order],
  );

  const columns: DataTableColumn<ClientRow>[] = [
    {
      // 1. Ügyfél — név (rendezhető). A státusz külön, dedikált UTOLSÓ oszlopban (egységes minden listán).
      id: 'name',
      header: t('clients.columns.client'),
      sortable: true,
      cardLabel: t('clients.columns.client'),
      width: 'minmax(0,2fr)',
      exportValue: (c) => c.name,
      cell: (c) => (
        <Text variant="itemTitle" as="div" className="truncate">
          {c.name}
        </Text>
      ),
    },
    {
      // 2. Fiókok — ≤3 statikus névlista; >3 legördülő (első 2 + …+N). Mobilon a „Részletek" alatt.
      id: 'accounts',
      header: t('clients.columns.accounts'),
      cardLabel: t('clients.columns.accounts'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      // A legördülő mögötti fiókok is: az export a TELJES listát adja, nem a látszó kettőt.
      exportValue: (c) => c.accounts.map((a) => a.name).join('\n'),
      cell: (c) =>
        c.accounts.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell
            items={c.accounts}
            getKey={(a) => a.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('clients.more', { count: n })}
            triggerAriaLabel={t('clients.moreItems')}
            renderItem={(a) => (
              // Fiók-név: másodlagos adat → a DataTable cella-stílusát örökli (13/muted), mint az accounts-oldalon.
              <span className="block truncate">{a.name}</span>
            )}
          />
        ),
    },
    {
      // 3. Számlázási adatok — 1 profil statikus; 2+ legördülő (első profil + +N). Mobilon a „Részletek" alatt.
      id: 'billingData',
      header: t('clients.columns.billingData'),
      cardLabel: t('clients.columns.billingData'),
      width: 'minmax(0,2.5fr)',
      multiline: true,
      // A profil szöveges alakja a CELLÁVAL EGY helyen dől el (`billingProfileExportText`); több profil
      // esetén üres sorral elválasztva, hogy a blokkok olvashatók maradjanak egy táblázat-cellában.
      exportValue: (c) => c.billingProfiles.map(billingProfileExportText).join('\n\n'),
      cell: (c) =>
        c.billingProfiles.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell
            items={c.billingProfiles}
            getKey={(p) => p.id}
            visible={1}
            threshold={1}
            moreLabel={(n) => t('clients.more', { count: n })}
            triggerAriaLabel={t('clients.moreItems')}
            renderItem={(p) => <BillingProfileCell profile={p} />}
          />
        ),
    },
    {
      // 4. Státusz — dedikált UTOLSÓ (adat) oszlop, a sor-akciók (⋯) előtt; pill-badge (egységes minden listán).
      id: 'status',
      header: t('clients.columns.status'),
      cardLabel: t('clients.columns.status'),
      width: 'minmax(0,1fr)',
      exportValue: (c) => t(`clients.statusLabel.${c.status}`, { defaultValue: c.status }),
      // Badge-cella: `multiline` → overflow-hidden (NEM truncate) → keskenyítéskor NINCS „…" a pill után.
      multiline: true,
      cell: (c) => (
        <ClientStatusBadge
          status={c.status}
          label={t(`clients.statusLabel.${c.status}`, { defaultValue: c.status })}
        />
      ),
    },
  ];

  const exportCtl = useListExport<ClientRow, number>({
    columns,
    slug: 'clients',
    fetchPage: fetchExportPage,
    // Az AKTÍV szűrők az audit-nyomba (a napló megmondja, MIT vitt el a felhasználó).
    filters: { q, status: status === ALL ? undefined : status },
  });

  return (
    <AppShell
      // Mobilon a lebegő speed-dial FAB-ban jelenik meg (Keresés · AI mellett) — csak ha van létrehozási jog.
      // Desktopon a toolbar-ban van a gomb (lásd lent, `hidden md:flex`).
      secondaryActions={[
        // MOBIL: a sáv export-gombja rejtve van — a FAB-ban elérhető marad (`xlsx`, formátum-választás
        // nélkül: a FAB egy koppintásos felület).
        {
          key: 'export',
          icon: <ArrowDownTrayIcon />,
          label: t('common.export.label'),
          onClick: () => exportCtl.run('xlsx'),
        },
      ]}
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('clients.new'),
              onClick: () => void navigate({ to: '/clients/new' }),
            }
          : undefined
      }
      toolbar={
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t('common.searchPlaceholder'),
            ariaLabel: t('common.searchPlaceholder'),
          }}
          filters={[
            {
              key: 'status',
              label: t('clients.filterStatus'),
              value: status,
              onChange: (v: string) => setFilters({ status: v }),
              options: STATUS_OPTIONS.map((s) => ({
                value: s,
                label: t(`clients.statusLabel.${s}`),
              })),
              allValue: ALL,
            },
          ]}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('clients.loading')
              : !isError && !infinite.isLoading
                ? t('clients.countOf', { shown: rows.length, total })
                : ''
          }
          actions={
            // A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást.
            <>
              <div className="hidden md:block">
                <ListExportButton
                  onExport={exportCtl.run}
                  pending={exportCtl.pending}
                  hasRows={!isError && rows.length > 0}
                />
              </div>
              <div className="hidden md:block">
                <ListNewButton
                  label={t('clients.new')}
                  onClick={() => void navigate({ to: '/clients/new' })}
                  enabled={canManage}
                  disabledTooltip={t('common.noCreatePermission')}
                />
              </div>
            </>
          }
        />
      }
    >
      {/* Lista — teljes szélesség, px-4 (a navbar-címmel egy vonalban). A konténer kitölti a main magasságát
          (h-full flex-col), a DataTable rögzített kártyája flex-1 → CSAK a tábla-test görög a kártyán belül
          (a page maga nem görög). */}
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<ClientRow>
          columns={columns}
          rows={rows}
          getRowId={(c) => c.id}
          sort="name"
          order={order}
          onSort={toggleOrder}
          isLoading={infinite.isLoading}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState title={t('clients.noMatches')} description={t('clients.noMatchesDesc')} />
            ) : (
              <EmptyState title={t('clients.empty')} description={t('clients.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          onRowClick={(c) => void navigate({ to: '/clients/$id', params: { id: c.id } })}
          rowActionsLabel={t('clients.columns.actions')}
          rowActions={(c) => (
            <RowActions>
              {canManage ? (
                <EditRowAction
                  label={t('clients.edit')}
                  onClick={() => void navigate({ to: '/clients/$id', params: { id: c.id } })}
                />
              ) : (
                <ViewRowAction
                  label={t('clients.view')}
                  onClick={() => void navigate({ to: '/clients/$id', params: { id: c.id } })}
                />
              )}
              {canDelete && (
                <DeleteRowAction label={t('clients.delete')} onClick={() => setDeleteTarget(c)} />
              )}
            </RowActions>
          )}
        />
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('clients.deleteTitle')}
        description={t('clients.deleteDesc', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('clients.delete')}
        cancelLabel={t('clients.cancel')}
        pendingLabel={t('clients.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
